import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell, Loader2, RefreshCw, Sparkles, Copy, CheckCircle2, X,
  Linkedin, Mail, Send, Clock, AlertCircle, Zap, ArrowRight, Users,
} from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";

const STAGE_LABELS = {
  soft: { label: "Soft (J+3)", color: "text-emerald-400", bg: "bg-emerald-500/10", tone: "Léger, respectueux — juste un petit rappel." },
  mid: { label: "Value (J+7)", color: "text-amber-400", bg: "bg-amber-500/10", tone: "Apporte de la valeur : insight, cas client, ressource." },
  last: { label: "Last-Call (J+14)", color: "text-red-400", bg: "bg-red-500/10", tone: "Dernier message clair et poli — sortie élégante." },
};

export default function Relances() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(null);
  const [generating, setGenerating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prospects/due-followups");
      setItems(data.prospects || []);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const generate = async (prospect, force) => {
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/prospects/${prospect.id}/generate-followup`,
        force ? { force_stage: force } : {},
        { timeout: 90000 },
      );
      setOpened(data);
      toast.success(`Relance ${data.pending_followup?.stage?.toUpperCase() || ""} générée`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération échouée");
    } finally {
      setGenerating(false);
    }
  };

  const markSent = async (prospect, channel) => {
    try {
      await api.post(`/prospects/${prospect.id}/mark-followup-sent`, { channel });
      toast.success("Relance marquée comme envoyée");
      setOpened(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <ConsoleShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="relances-page">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Clara · Relances intelligentes
            </div>
            {items.length > 0 && (
              <span className="text-[10px] bg-red-500/20 text-red-300 px-1.5 py-0.5 uppercase tracking-wider">
                {items.length} à envoyer
              </span>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-light text-white tracking-tight mt-1">
            Relances à envoyer
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Cadence auto : <span className="text-emerald-400">Soft J+3</span> → <span className="text-amber-400">Value J+7</span> → <span className="text-red-400">Last-Call J+14</span>.
            Clara rédige la relance adaptée au stade — tu copies, tu envoies, tu passes au suivant.
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-zinc-500">
            {loading ? "Chargement…" : `${items.length} prospect${items.length > 1 ? "s" : ""} en attente de relance`}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/app/prospects"
              className="text-xs inline-flex items-center gap-1 text-zinc-400 hover:text-white"
              data-testid="link-prospects"
            >
              <Users className="w-3.5 h-3.5" />
              Tous les prospects
            </Link>
            <button
              onClick={refresh}
              className="text-xs inline-flex items-center gap-1 text-zinc-400 hover:text-white"
              aria-label="Rafraîchir"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Actualiser
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-zinc-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Chargement…
          </div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-2">
            {items.map((p) => (
              <ProspectRow
                key={p.id}
                p={p}
                onGenerate={() => generate(p)}
                onOpen={() => setOpened(p.pending_followup ? p : null)}
                generating={generating}
              />
            ))}
          </div>
        )}
      </div>

      {opened && (
        <FollowupDrawer
          prospect={opened}
          onClose={() => setOpened(null)}
          onRegenerate={(force) => generate(opened, force)}
          onMarkSent={(channel) => markSent(opened, channel)}
          generating={generating}
        />
      )}
    </ConsoleShell>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-white/10 p-10 text-center space-y-3" data-testid="empty-state">
      <Bell className="w-8 h-8 text-zinc-600 mx-auto" />
      <div className="text-sm text-zinc-300">Aucune relance en attente 🎉</div>
      <div className="text-xs text-zinc-500 max-w-md mx-auto">
        Marque des prospects comme <span className="text-blue-300">Contacté</span> sur la page{" "}
        <Link to="/app/prospects" className="underline hover:text-white">Prospects</Link> pour démarrer
        automatiquement la cadence de relance.
      </div>
    </div>
  );
}

function ProspectRow({ p, onGenerate, onOpen, generating }) {
  const st = p.followup_state || {};
  const stageDef = STAGE_LABELS[st.stage] || STAGE_LABELS.soft;
  const hasPending = !!p.pending_followup;

  return (
    <div
      className="border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition p-4 flex items-start gap-4"
      data-testid={`prospect-row-${p.id}`}
    >
      <div className={`w-1 self-stretch ${stageDef.bg}`} />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-white font-medium">{p.full_name}</span>
          {p.company && <span className="text-xs text-zinc-500">· {p.company}</span>}
          {p.role && <span className="text-[10px] text-zinc-600 uppercase tracking-wider">· {p.role}</span>}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className={`inline-flex items-center gap-1 ${stageDef.color}`}>
            <Zap className="w-3 h-3" />
            {stageDef.label}
          </span>
          {st.days_overdue > 0 && (
            <span className="inline-flex items-center gap-1 text-red-400">
              <AlertCircle className="w-3 h-3" />
              Overdue de {st.days_overdue}j
            </span>
          )}
          {st.days_overdue === 0 && (
            <span className="inline-flex items-center gap-1 text-zinc-400">
              <Clock className="w-3 h-3" />
              Dû aujourd&apos;hui
            </span>
          )}
          {p.contacted_at && (
            <span className="text-zinc-600">
              Contacté le {p.contacted_at.slice(0, 10)}
            </span>
          )}
        </div>
        <div className="text-[11px] text-zinc-600">{stageDef.tone}</div>
      </div>
      <div className="flex flex-col sm:flex-row items-end gap-2">
        {hasPending ? (
          <button
            onClick={onOpen}
            data-testid={`open-${p.id}`}
            className="inline-flex items-center gap-2 bg-emerald-500 text-black px-4 py-2 text-xs font-medium hover:bg-emerald-400 transition"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Voir la relance
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            onClick={onGenerate}
            disabled={generating}
            data-testid={`generate-${p.id}`}
            className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 text-xs font-medium hover:bg-zinc-200 transition disabled:opacity-40"
          >
            {generating ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Clara rédige…
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                Générer la relance
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function FollowupDrawer({ prospect, onClose, onRegenerate, onMarkSent, generating }) {
  const [copied, setCopied] = useState(null); // "linkedin" | "email"
  const pf = prospect.pending_followup || {};
  const stageDef = STAGE_LABELS[pf.stage] || STAGE_LABELS.soft;

  const copy = async (channel, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(channel);
      toast.success("Copié — colle où tu veux !");
      setTimeout(() => setCopied(null), 2500);
    } catch {
      toast.error("Copie impossible");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-black border border-white/10 w-full max-w-2xl max-h-screen sm:max-h-[90vh] overflow-y-auto" data-testid="followup-drawer">
        <div className="sticky top-0 bg-black border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Relance {stageDef.label}
            </div>
            <div className="text-sm text-white font-medium">{prospect.full_name}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5" data-testid="drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* LinkedIn version */}
          {pf.linkedin_followup && (
            <div className="border border-blue-500/20 bg-blue-500/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-blue-300 text-xs uppercase tracking-wider">
                  <Linkedin className="w-3.5 h-3.5" />
                  Version LinkedIn
                </div>
                <span className="text-[10px] text-zinc-600">{pf.linkedin_followup.length} car.</span>
              </div>
              <div className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed">
                {pf.linkedin_followup}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => copy("linkedin", pf.linkedin_followup)}
                  data-testid="copy-linkedin"
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-medium transition ${
                    copied === "linkedin" ? "bg-emerald-500 text-white" : "bg-white text-black hover:bg-zinc-200"
                  }`}
                >
                  {copied === "linkedin" ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier LinkedIn</>}
                </button>
                <button
                  onClick={() => onMarkSent("linkedin")}
                  data-testid="mark-sent-linkedin"
                  className="inline-flex items-center gap-2 border border-emerald-500/40 text-emerald-300 px-4 py-2.5 text-xs hover:bg-emerald-500/10 transition"
                >
                  <Send className="w-3.5 h-3.5" />
                  J&apos;ai envoyé
                </button>
              </div>
            </div>
          )}

          {/* Email version */}
          {pf.email_followup && (
            <div className="border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-wider">
                  <Mail className="w-3.5 h-3.5" />
                  Version Email
                </div>
              </div>
              <div className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed font-mono">
                {pf.email_followup}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => copy("email", pf.email_followup)}
                  data-testid="copy-email"
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-medium transition ${
                    copied === "email" ? "bg-emerald-500 text-white" : "bg-white text-black hover:bg-zinc-200"
                  }`}
                >
                  {copied === "email" ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier email</>}
                </button>
                <button
                  onClick={() => onMarkSent("email")}
                  data-testid="mark-sent-email"
                  className="inline-flex items-center gap-2 border border-emerald-500/40 text-emerald-300 px-4 py-2.5 text-xs hover:bg-emerald-500/10 transition"
                >
                  <Send className="w-3.5 h-3.5" />
                  J&apos;ai envoyé
                </button>
              </div>
            </div>
          )}

          {/* Regenerate with force stage */}
          <div className="border-t border-white/5 pt-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
              Pas le bon ton ? Force un autre stade
            </div>
            <div className="flex gap-2 flex-wrap">
              {["soft", "mid", "last"].map((s) => (
                <button
                  key={s}
                  onClick={() => onRegenerate(s)}
                  disabled={generating}
                  data-testid={`force-${s}`}
                  className={`text-xs px-3 py-1.5 border transition ${
                    pf.stage === s
                      ? "border-white/30 text-white bg-white/5"
                      : "border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
                  } disabled:opacity-40`}
                >
                  {STAGE_LABELS[s].label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
